import { addDays, format, parseISO } from 'date-fns';

/**
 * E47 · Schema modes on the portal — the hub's Modes tab (ClientModeTimeline), the "Set up the modes with the client"
 * section (ConfigureModeSet, subject = the client) and the "Assign daily check-ins" section (E34 AssignHomework, target
 * modeCheckIn). The field lists the dynamic GraphQL client sends, the types of what comes back, and the pure bits the
 * screens share (the range as a query input, the stacked days, the set-up draft and its command input).
 *
 * The portal does not bundle `@cc/domain`, so the few constants of `packages/domain/src/schemaModes/modes.ts` are copied
 * here (the ids and kinds only — the NAMES are `modes.catalog.<modeId>.name`, never data). A drift between the two is a
 * model defect: `model.test.ts` pins the copy against the board's list (fourteen, the common seven, six own modes).
 */

// ── the vocabulary (copy of @cc/domain's schemaModes/modes.ts) ──────────────────────────────────────

export type ModeKind =
  | 'child'
  | 'coping'
  | 'critic'
  | 'healthyAdult'
  | 'happyChild';
export type ModeGroup = 'child' | 'coping' | 'critic' | 'healthy';

export const OWN_MODE_KINDS = ['child', 'coping', 'critic'] as const;
export type OwnModeKind = (typeof OWN_MODE_KINDS)[number];
export const HEALTHY_ADULT_ID = 'ha';
export const MAX_OWN_MODES = 6;
export const MAX_MODES = 20;

export const STANDARD_MODES: ReadonlyArray<{
  modeId: string;
  kind: ModeKind;
  group: ModeGroup;
}> = [
  { modeId: 'vc', kind: 'child', group: 'child' },
  { modeId: 'ac', kind: 'child', group: 'child' },
  { modeId: 'ec', kind: 'child', group: 'child' },
  { modeId: 'ic', kind: 'child', group: 'child' },
  { modeId: 'uc', kind: 'child', group: 'child' },
  { modeId: 'hc', kind: 'happyChild', group: 'child' },
  { modeId: 'cs', kind: 'coping', group: 'coping' },
  { modeId: 'dp', kind: 'coping', group: 'coping' },
  { modeId: 'ds', kind: 'coping', group: 'coping' },
  { modeId: 'sa', kind: 'coping', group: 'coping' },
  { modeId: 'ba', kind: 'coping', group: 'coping' },
  { modeId: 'pc', kind: 'critic', group: 'critic' },
  { modeId: 'dc', kind: 'critic', group: 'critic' },
  { modeId: 'ha', kind: 'healthyAdult', group: 'healthy' },
];

export const MODE_GROUPS: readonly ModeGroup[] = [
  'child',
  'coping',
  'critic',
  'healthy',
];

export const COMMON_SEVEN_IDS: readonly string[] = [
  'vc',
  'ac',
  'hc',
  'dp',
  'pc',
  'dc',
  'ha',
];

export const DEFAULT_MODE_COLOURS: Readonly<Record<string, string>> = {
  vc: '#8BA88E',
  ac: '#C98A5A',
  ec: '#B5513F',
  ic: '#D4A24C',
  uc: '#A89B7A',
  hc: '#E0B84A',
  cs: '#8A93B5',
  dp: '#7C8A99',
  ds: '#9A8AA8',
  sa: '#B08A6A',
  ba: '#A55C5C',
  pc: '#B85C5C',
  dc: '#C27A6A',
  ha: '#5C8E7A',
};

/** The colour chips a mode can take (the app's palette; the current colour is always offered too). */
export const COLOUR_CHIPS: readonly string[] = [
  '#8BA88E',
  '#C98A5A',
  '#B5513F',
  '#D4A24C',
  '#E0B84A',
  '#8A93B5',
  '#7C8A99',
  '#9A8AA8',
  '#B85C5C',
  '#5C8E7A',
];

export const FALLBACK_COLOUR = '#9AA0A6';
const OWN_ID = /^own-(\d+)$/;

// ── the wire ────────────────────────────────────────────────────────────────────────────────────────

const MODE_MAP_FIELDS = [
  'modeId',
  'kind',
  'label',
  'colour',
  'own',
  'cardId',
  'cardText',
  'switchedOff',
];
const LAST_CHANGE_FIELDS = [
  'performer',
  'byClient',
  'performerName',
  'occurredAt',
];

export const TIMELINE_FIELDS = [
  'subject',
  'from',
  'to',
  { modeMap: MODE_MAP_FIELDS },
  { modeDays: ['date', { modes: ['modeId', 'peak'] }] },
  { triggerMatrix: ['trigger', { modes: ['modeId', 'count'] }] },
  {
    healthyAdultShare: ['week', 'share', 'presentCheckIns', 'checkIns', 'mean'],
  },
  'checkIns',
  'checkInsThisWeek',
  { lastChange: LAST_CHANGE_FIELDS },
];

export const SET_FIELDS = [
  'subject',
  'configured',
  { modeMap: MODE_MAP_FIELDS },
  { lastChange: LAST_CHANGE_FIELDS },
];

export const COMMAND_FIELDS = ['id', 'recorded'];

export type ModeMapEntry = {
  modeId: string;
  kind: ModeKind;
  label?: string | null;
  colour?: string | null;
  own: boolean;
  cardId?: string | null;
  cardText?: string | null;
  switchedOff: boolean;
};

export type LastChange = {
  performer: string;
  byClient: boolean;
  performerName?: string | null;
  occurredAt: string;
};

export type ModeDay = {
  date: string;
  modes: Array<{ modeId: string; peak: number }>;
};
export type TriggerRow = {
  trigger: string;
  modes: Array<{ modeId: string; count: number }>;
};
export type HealthyAdultWeek = {
  week: string;
  share?: number | null;
  presentCheckIns: number;
  checkIns: number;
  mean?: number | null;
};

export type TimelineRow = {
  subject: string;
  from: string;
  to: string;
  modeMap: ModeMapEntry[];
  modeDays: ModeDay[];
  triggerMatrix: TriggerRow[];
  healthyAdultShare: HealthyAdultWeek[];
  checkIns: number;
  checkInsThisWeek: number;
  lastChange?: LastChange | null;
};

export type SetRow = {
  subject: string;
  configured: boolean;
  modeMap: ModeMapEntry[];
  lastChange?: LastChange | null;
};

// ── the period: a real query input ──────────────────────────────────────────────────────────────────

export const PERIODS = [7, 30, 90, 'custom'] as const;
export type Period = (typeof PERIODS)[number];
export const DEFAULT_PERIOD: Period = 30;

export const dayOf = (d: Date): string => format(d, 'yyyy-MM-dd');

/**
 * The range as the query's variables: `days` for a preset, inclusive local `from`/`to` dates for a custom range. A custom
 * range not picked yet (or reversed) is `null` — the screen keeps the previous answer instead of asking for nothing.
 */
export const rangeVariables = (
  period: Period,
  custom: { from: Date; to: Date } | null,
): { days: number } | { from: string; to: string } | null => {
  if (period !== 'custom') return { days: period };
  if (!custom) return null;
  const [from, to] = [dayOf(custom.from), dayOf(custom.to)];
  return from <= to ? { from, to } : { from: to, to: from };
};

/** Every local date of `from`..`to` (inclusive), so a day nobody checked in on is a visible gap. */
export const daysBetween = (from: string, to: string): string[] => {
  const out: string[] = [];
  let d = parseISO(from);
  const end = parseISO(to);
  // a range is at most a year (the API clamps it); guard anyway
  for (let i = 0; i < 400 && d <= end; i += 1) {
    out.push(dayOf(d));
    d = addDays(d, 1);
  }
  return out;
};

// ── the mode map ────────────────────────────────────────────────────────────────────────────────────

/** The name shown: the person's own words, else the app's default name of a standard mode (i18n, never data). */
export const modeName = (
  entry: Pick<ModeMapEntry, 'modeId' | 'label'>,
  t: (key: string) => string,
): string => {
  const words = entry.label?.trim();
  if (words) return words;
  return STANDARD_MODES.some((m) => m.modeId === entry.modeId)
    ? t(`modes.catalog.${entry.modeId}.name`)
    : entry.modeId;
};

export const colourOf = (
  entry: Pick<ModeMapEntry, 'modeId' | 'colour'>,
): string =>
  entry.colour || DEFAULT_MODE_COLOURS[entry.modeId] || FALLBACK_COLOUR;

/** The map in the order the screens read: the current set first (standard order, then own), the switched-off ones last. */
export const orderedMap = (map: readonly ModeMapEntry[]): ModeMapEntry[] => {
  const rank = (m: ModeMapEntry) => {
    const std = STANDARD_MODES.findIndex((s) => s.modeId === m.modeId);
    return std >= 0 ? std : 100 + Number(OWN_ID.exec(m.modeId)?.[1] ?? 0);
  };
  return [...map].sort(
    (a, b) =>
      Number(a.switchedOff) - Number(b.switchedOff) || rank(a) - rank(b),
  );
};

// ── the whole period: one bar per mode (the phone's My week, ported) ───────────────────────────────

/** The strongest rating a check-in can carry (a copy of @cc/domain's `MODE_INTENSITY_MAX`; `model.test.ts` pins them equal). */
export const MODE_INTENSITY_MAX = 10;

/** One row of THE WHOLE PERIOD: a mode that showed up, on how many days, how strong at most. */
export type PeriodMode = {
  mode: Pick<ModeMapEntry, 'modeId' | 'label' | 'kind' | 'switchedOff'> &
    Partial<ModeMapEntry>;
  days: number;
  peak: number;
  colour: string;
  /** 0..100 — the strongest rating as a share of the scale. */
  widthPct: number;
};

/**
 * `periodModesOf` of the phone (`packages/mobile/.../schemaModes/modesModel.ts`): over the days of the chosen range, each mode that
 * showed up with the number of days it was here and its highest peak, sorted by days, then peak, then the set's order (`orderedMap`).
 * A mode switched off keeps its row (the screen greys it).
 */
export const periodModesOf = (
  modeDays: readonly ModeDay[],
  modeMap: readonly ModeMapEntry[],
): PeriodMode[] => {
  const totals = new Map<string, { days: number; peak: number }>();
  for (const day of modeDays) {
    for (const m of day.modes) {
      const t = totals.get(m.modeId) ?? { days: 0, peak: 0 };
      totals.set(m.modeId, {
        days: t.days + 1,
        peak: Math.max(t.peak, m.peak),
      });
    }
  }
  const order = new Map(orderedMap(modeMap).map((m, i) => [m.modeId, i]));
  return Array.from(totals.entries())
    .map(([modeId, t]) => {
      const known = modeMap.find((m) => m.modeId === modeId);
      const mode: PeriodMode['mode'] = known ?? {
        modeId,
        label: null,
        kind: 'child',
        switchedOff: false,
      };
      return {
        mode,
        days: t.days,
        peak: t.peak,
        colour: colourOf({ modeId, colour: known?.colour }),
        widthPct: Math.round(
          (Math.min(MODE_INTENSITY_MAX, Math.max(0, t.peak)) /
            MODE_INTENSITY_MAX) *
            100,
        ),
      };
    })
    .sort(
      (a, b) =>
        b.days - a.days ||
        b.peak - a.peak ||
        (order.get(a.mode.modeId) ?? 99) - (order.get(b.mode.modeId) ?? 99),
    );
};

/**
 * One Healthy Adult week, the phone's three cases (`healthyAdultLineOf`, ModesWeekScreen): rated (a share), NOT RATED while other modes
 * were checked in (no percentage, no "present in 0 of n" — Q-e47p-1 A), and nothing that week (null).
 */
export type HealthyAdultLine = {
  sharePct: number | null;
  notRated: boolean;
  present: number;
  total: number;
  mean: number | null;
};

export const healthyAdultLineOfWeek = (
  w: HealthyAdultWeek | undefined,
): HealthyAdultLine | null => {
  if (!w || w.checkIns <= 0) return null;
  const hasShare = w.share !== null && w.share !== undefined;
  const notRated = hasShare && w.presentCheckIns === 0;
  return {
    sharePct:
      hasShare && !notRated ? Math.round((w.share as number) * 100) : null,
    notRated,
    present: w.presentCheckIns,
    total: w.checkIns,
    mean: w.mean ?? null,
  };
};

/** The phone's `healthyAdultLineOf`: this week's line — the LAST row of the weekly figures. */
export const healthyAdultLineOf = (
  weeks: readonly HealthyAdultWeek[],
): HealthyAdultLine | null => healthyAdultLineOfWeek(weeks[weeks.length - 1]);

// ── the stacked timeline ────────────────────────────────────────────────────────────────────────────

export type Segment = {
  modeId: string;
  peak: number;
  y: number;
  height: number;
};
export type Bar = { date: string; total: number; segments: Segment[] };

/**
 * One bar per local day of the range: each mode's peak that day stacked in the map's order. `scale` is the tallest bar's
 * total (min 1), so the heights read against one axis; a day with no check-in is an empty bar.
 */
export const stackBars = (
  dates: readonly string[],
  modeDays: readonly ModeDay[],
  order: readonly string[],
): { bars: Bar[]; scale: number } => {
  const byDate = new Map(modeDays.map((d) => [d.date, d.modes]));
  const rank = (id: string) => {
    const i = order.indexOf(id);
    return i < 0 ? order.length : i;
  };
  const raw = dates.map((date) => {
    const modes = [...(byDate.get(date) ?? [])]
      .filter((m) => m.peak > 0)
      .sort((a, b) => rank(a.modeId) - rank(b.modeId));
    return { date, modes, total: modes.reduce((n, m) => n + m.peak, 0) };
  });
  const scale = Math.max(1, ...raw.map((r) => r.total));
  const bars = raw.map((r) => {
    let y = 0;
    const segments = r.modes.map((m) => {
      const seg = { modeId: m.modeId, peak: m.peak, y, height: m.peak / scale };
      y += seg.height;
      return seg;
    });
    return { date: r.date, total: r.total, segments };
  });
  return { bars, scale };
};

/** The modes that have a count in the matrix, in the map's order. */
export const matrixColumns = (
  rows: readonly TriggerRow[],
  order: readonly string[],
): string[] => {
  const ids = new Set(rows.flatMap((r) => r.modes.map((m) => m.modeId)));
  const rank = (id: string) => {
    const i = order.indexOf(id);
    return i < 0 ? order.length : i;
  };
  return Array.from(ids).sort((a, b) => rank(a) - rank(b));
};

/** Whole percent of a share (0–1); `null` when there is no figure — never 0 % for a week with nothing rated. */
export const percentOf = (share: number | null | undefined): number | null =>
  share === null || share === undefined ? null : Math.round(share * 100);

/** One decimal, trailing ".0" dropped (4.2, 3). */
export const oneDecimal = (n: number): string =>
  String(Math.round(n * 10) / 10);

export type ChangeLine =
  | { key: 'client' | 'you' | 'therapistNoName' | 'never'; name?: undefined }
  | { key: 'therapist'; name: string };

/** Who changed the set last: "the client" / "Dr. …" / "you" (the viewer's own change). */
export const changeLine = (
  change: LastChange | null | undefined,
  viewerId: string | null | undefined,
): ChangeLine => {
  if (!change) return { key: 'never' };
  if (viewerId && change.performer === viewerId) return { key: 'you' };
  if (change.byClient) return { key: 'client' };
  const name = change.performerName?.trim();
  return name ? { key: 'therapist', name } : { key: 'therapistNoName' };
};

// ── the set-up draft ────────────────────────────────────────────────────────────────────────────────

export type StandardDraft = { on: boolean; words: string; colour: string };
export type OwnDraft = {
  modeId: string;
  kind: OwnModeKind;
  words: string;
  colour: string;
};
export type SetDraft = {
  standard: Record<string, StandardDraft>;
  own: OwnDraft[];
};

const isOwnKind = (k: string): k is OwnModeKind =>
  (OWN_MODE_KINDS as readonly string[]).includes(k);

/**
 * The draft that starts from the client's CURRENT set (so the latest edit is what the therapist sees and builds on): the
 * modes in it are on, with their words and colours; the rest are off with the default colour. A switched-off entry (only a
 * timeline carries one) is not part of the set.
 */
export const draftFromSet = (map: readonly ModeMapEntry[]): SetDraft => {
  const live = map.filter((m) => !m.switchedOff);
  const current = new Map(live.map((m) => [m.modeId, m]));
  const standard: Record<string, StandardDraft> = {};
  for (const s of STANDARD_MODES) {
    const m = current.get(s.modeId);
    standard[s.modeId] = {
      on: s.modeId === HEALTHY_ADULT_ID || !!m,
      words: m?.label?.trim() ?? '',
      colour: m?.colour || DEFAULT_MODE_COLOURS[s.modeId],
    };
  }
  const own = orderedMap(live)
    .filter((m) => m.own && OWN_ID.test(m.modeId) && isOwnKind(m.kind))
    .map((m) => ({
      modeId: m.modeId,
      kind: m.kind as OwnModeKind,
      words: m.label?.trim() ?? '',
      colour: m.colour || FALLBACK_COLOUR,
    }));
  return { standard, own };
};

export const applyPreset = (
  draft: SetDraft,
  preset: 'commonSeven' | 'allFourteen',
): SetDraft => ({
  ...draft,
  standard: Object.fromEntries(
    STANDARD_MODES.map((s) => [
      s.modeId,
      {
        ...draft.standard[s.modeId],
        on:
          s.modeId === HEALTHY_ADULT_ID ||
          preset === 'allFourteen' ||
          COMMON_SEVEN_IDS.includes(s.modeId),
      },
    ]),
  ),
});

/** The ids of the person's own modes anywhere in these maps — live OR switched off (a switched-off one keeps its past check-ins). */
export const takenOwnIds = (map: readonly ModeMapEntry[]): string[] =>
  Array.from(
    new Set(map.filter((m) => OWN_ID.test(m.modeId)).map((m) => m.modeId)),
  );

/**
 * The next `own-<n>` id: one past the highest in the draft OR in `takenIds` (modes switched off earlier, or removed in this very
 * draft, which keep their past check-ins) — a new mode never inherits an old one's history. The phone's `addOwnMode` does the same.
 * (N-6, 2026-10-22: it used to look at the draft's own modes only and so could hand out a switched-off mode's id again.)
 */
export const nextOwnId = (
  own: readonly OwnDraft[],
  takenIds: readonly string[] = [],
): string => {
  const numbers = [...own.map((o) => o.modeId), ...takenIds].map((id) =>
    Number(OWN_ID.exec(id)?.[1] ?? 0),
  );
  return `own-${Math.max(0, ...numbers) + 1}`;
};

export const canAddOwn = (draft: SetDraft): boolean =>
  draft.own.length < MAX_OWN_MODES;

export const addOwnMode = (
  draft: SetDraft,
  takenIds: readonly string[] = [],
): SetDraft =>
  canAddOwn(draft)
    ? {
        ...draft,
        own: [
          ...draft.own,
          {
            modeId: nextOwnId(draft.own, takenIds),
            kind: 'child',
            words: '',
            colour: COLOUR_CHIPS[0],
          },
        ],
      }
    : draft;

/** An own mode needs a name; the Healthy Adult is on by construction. */
export const draftProblem = (draft: SetDraft): 'needsName' | null =>
  draft.own.some((o) => !o.words.trim()) ? 'needsName' : null;

export type ModeInputRow = {
  modeId: string;
  kind: string;
  label: string;
  colour: string;
  own?: boolean;
};

/**
 * The draft as ConfigureModeSet's input (subject = the client): the modes that are on in the standard order, then the
 * client's own. A mode nobody renamed carries the app's default name in the therapist's language (`defaultName`), as the
 * contract says — the person's words when there are some.
 */
export const setInputOf = (
  subject: string,
  draft: SetDraft,
  defaultName: (modeId: string) => string,
): { subject: string; modes: ModeInputRow[] } => ({
  subject,
  modes: [
    ...STANDARD_MODES.filter((s) => draft.standard[s.modeId]?.on).map((s) => ({
      modeId: s.modeId,
      kind: s.kind,
      label: draft.standard[s.modeId].words.trim() || defaultName(s.modeId),
      colour: draft.standard[s.modeId].colour,
    })),
    ...draft.own.map((o) => ({
      modeId: o.modeId,
      kind: o.kind,
      label: o.words.trim(),
      colour: o.colour,
      own: true,
    })),
  ],
});

// ── assign daily check-ins ──────────────────────────────────────────────────────────────────────────

export const ASSIGN_DAYS = 7;

/** "Every day for 7 days": from tomorrow, 09:00 in the CLIENT's zone (the E34 repeat rule; the board's example). */
export const modeCheckInSchedule = (
  from: Date,
  tzid: string,
): { rrule: string; dtstart: string; tzid: string; exdates: string[] } => ({
  rrule: `FREQ=DAILY;COUNT=${ASSIGN_DAYS}`,
  dtstart: `${dayOf(addDays(from, 1))}T09:00:00`,
  tzid,
  exdates: [],
});
